// @ts-nocheck

/*
 * Sticky Add to Cart
 *
 * POLICY: the subscribe toggle always starts OFF. There is no setting for this
 * and that is deliberate.
 *
 * It used to be a per-template setting (default_purchase_type). 18 of 30
 * product templates never saved it, so it silently fell back to 'subscription':
 * the bar showed the discounted price while the buy box showed the one-time
 * price, and Add To Cart posted a selling_plan. Customers were enrolled in
 * recurring plans they never chose, on pages with no subscription selector at
 * all. Verified live on Break-FAST (7) on 2026-08-06.
 *
 * A subscription is now something the customer switches on, or something the
 * Sub Buy Box tells us they selected. It is never an initial state.
 *
 * To deliberately pre-select subscribe on a specific template, change the line
 * marked INITIAL PURCHASE TYPE below -- and only after checking that template
 * shows the customer, unambiguously, that they are subscribing.
 */

document.addEventListener('alpine:init', () => {
  const approvedPlanIds = new Set([2274525299, 2274558067, 2274590835, 2274623603]);
  Alpine.data('ctrStickyAtc', () => ({
    /* R21 (12 Aug 2026) -- VISIBILITY IS NOW THREE INPUTS, NOT ONE.
     *
     * `visible` used to be a plain property set straight from the
     * IntersectionObserver on the buy box. It is now a getter over:
     *
     *   pastTarget   the original signal: the buy box has scrolled out of view
     *   dismissed    the customer swiped the bar down to get it out of the way
     *   scrollingUp  they are scrolling back up, i.e. heading toward the real
     *                buy box, so the bar is not needed
     *
     * The template still binds `visible`, unchanged.
     *
     * `dismissed` resets when the buy box comes back into view, so a stray
     * swipe cannot leave someone with no add-to-cart for the rest of the page.
     */
    pastTarget: false,
    dismissed: false,
    scrollingUp: false,

    get visible() {
      return this.pastTarget && !this.dismissed && !this.scrollingUp;
    },

    _observer: null,
    _lastScrollY: 0,
    _scrollQueued: false,
    _touchStartY: null,
    _touchDeltaY: 0,

    // State synced from the buy box
    purchaseType: 'one-time',
    currentSellingPlanId: null,
    variantId: null,
    variantPrice: 0,
    displayPrice: 0,
    compareAtPrice: null,
    sellingPlanAllocations: [],
    variantAvailable: true,
    hasSellingPlans: false,

    /* R21b: the frequency popover.
     *
     * `plans` is the SELECTABLE list, derived from sellingPlanAllocations, not
     * a separate source of truth and never a hardcoded id -- the same interval
     * has different ids in different selling plan groups (4-weekly has been
     * seen as 2650341491 and 2274623603 on the same product). Rebuilt on every
     * variant change.
     */
    plans: [],
    freqOpen: false,

    buttonText: 'Add to cart',
    oneTimeLabel: 'One-time',
    subscribeLabelBase: 'Subscribe & save',
    savingPct: 0,

    loading: false,

    init() {
      this._readDataAttributes();

      this._setupObserver();

      // --- R21: scroll direction -------------------------------------------
      // 10px threshold so a nudge, or iOS rubber-banding at either end of the
      // page, does not flip the bar back and forth.
      this._lastScrollY = window.scrollY;
      this._onScroll = () => {
        if (this._scrollQueued) return;
        this._scrollQueued = true;
        requestAnimationFrame(() => {
          this._scrollQueued = false;
          const y = window.scrollY;
          const dy = y - this._lastScrollY;
          if (Math.abs(dy) < 10) return;
          this.scrollingUp = dy < 0;
          this._lastScrollY = y;
        });
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });

      // --- R21: swipe down to dismiss --------------------------------------
      // Never calls preventDefault, so the page still scrolls normally and a
      // tap on ADD TO CART (delta ~0) can never register as a swipe.
      //
      // R21b: touches that start inside the frequency popover are ignored.
      // The popover is a child of this element, so without the guard a drag
      // across the options list dismisses the whole bar.
      this._onTouchStart = (e) => {
        if (e.target.closest && e.target.closest('.ctr-satc__pop')) {
          this._touchStartY = null;
          return;
        }
        this._touchStartY = e.touches[0].clientY;
        this._touchDeltaY = 0;
      };
      this._onTouchMove = (e) => {
        if (this._touchStartY === null) return;
        this._touchDeltaY = e.touches[0].clientY - this._touchStartY;
      };
      this._onTouchEnd = () => {
        if (this._touchDeltaY > 60) this.dismissed = true;
        this._touchStartY = null;
        this._touchDeltaY = 0;
      };
      this.$el.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.$el.addEventListener('touchmove', this._onTouchMove, { passive: true });
      this.$el.addEventListener('touchend', this._onTouchEnd, { passive: true });
      this.$el.addEventListener('touchcancel', this._onTouchEnd, { passive: true });

      // --- R21: publish geometry for the chat launcher ----------------------
      // snippets/ctr-gorgias-launcher.liquid lifts the chat bubble clear of
      // this bar using the body class and the height variable set here.
      //
      // The bar is a fixed height in both toggle states by design, so this
      // reads the same number before and after a toggle. If a change to the
      // markup makes the bar grow, the bubble will jump mid-scroll.
      Alpine.effect(() => {
        const showing = this.visible;
        document.body.classList.toggle('ctr-sticky-atc-open', showing);
        if (showing) {
          this._measure();
        } else if (this.freqOpen) {
          // A hidden bar must not leave a popover floating over the page.
          this.freqOpen = false;
        }
      });

      this._attrObserver = new MutationObserver(() => {
        this._readDataAttributes({ preservePurchaseType: true });
        this._setupObserver();
        document.dispatchEvent(new CustomEvent('ctr:buybox:request-state'));
      });
      this._attrObserver.observe(this.$el, {
        attributes: true,
        attributeFilter: [
          'data-variant-id',
          'data-price',
          'data-compare-at-price',
          'data-has-selling-plans',
          'data-default-selling-plan-id',
          'data-selling-plan-allocations',
          'data-target-selector',
        ],
      });

      // Sync whenever the variant picker fires a new variant
      this._onVariantUpdate = (e) => {
        const variant = e.detail?.resource;
        if (!variant) return;

        this.variantId = variant.id;
        this.variantPrice = variant.price || 0;
        this.variantAvailable = variant.available !== false;

        const allocations = (variant.selling_plan_allocations || [])
          .filter((a) => approvedPlanIds.has(Number(a.selling_plan_id)))
          .map((a) => ({
            selling_plan_id: a.selling_plan_id,
            price: a.price,
            compare_at_price: a.compare_at_price,
            name: a.selling_plan_name || (a.selling_plan && a.selling_plan.name) || '',
          }));
        this.sellingPlanAllocations = allocations;
        this.hasSellingPlans = allocations.length > 0;
        this._buildPlans();

        if (allocations.length > 0) {
          const stillValid = allocations.find((a) => a.selling_plan_id === this.currentSellingPlanId);
          if (!stillValid) this.currentSellingPlanId = this._defaultPlanId();
        } else {
          this.currentSellingPlanId = null;
          this.purchaseType = 'one-time';
          this.freqOpen = false;
        }

        this._refreshPrices();
        this.$nextTick(() => {
          this._setupObserver();
          this._measure();
        });
      };

      // Sync whenever the buy box changes purchase type / selling plan frequency.
      // This is the one path that may legitimately switch us into 'subscription'
      // on load: it means the customer picked it in the buy box.
      this._onBuyBoxChange = (e) => {
        const detail = e.detail || {};

        // Ignore our own broadcasts. Without this the event we fire when the
        // customer flips the toggle or picks a frequency comes straight back to
        // us, and any listener that re-broadcasts its own (stale) state
        // overwrites the change the customer just made. Cost me a frequency
        // selection that silently reverted to the default plan.
        if (detail.source === 'sticky-atc') return;

        if (detail.purchaseType !== undefined) this.purchaseType = detail.purchaseType;
        if (detail.currentSellingPlanId !== undefined) this.currentSellingPlanId = detail.currentSellingPlanId;
        if (detail.sellingPlanAllocations !== undefined) {
          this.sellingPlanAllocations = detail.sellingPlanAllocations;
          this._buildPlans();
        }
        if (detail.variantId !== undefined) this.variantId = detail.variantId;
        if (detail.variantPrice !== undefined) this.variantPrice = detail.variantPrice;
        this._refreshPrices();
      };

      document.addEventListener('variant:update', this._onVariantUpdate);
      document.addEventListener('ctr:buybox:change', this._onBuyBoxChange);

      // Ask the buy box to broadcast its current state immediately
      // (handles the case where the buy box finished init before us)
      document.dispatchEvent(new CustomEvent('ctr:buybox:request-state'));
    },

    destroy() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._attrObserver) {
        this._attrObserver.disconnect();
        this._attrObserver = null;
      }
      document.removeEventListener('variant:update', this._onVariantUpdate);
      document.removeEventListener('ctr:buybox:change', this._onBuyBoxChange);

      // R21 teardown
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onTouchStart) {
        this.$el.removeEventListener('touchstart', this._onTouchStart);
        this.$el.removeEventListener('touchmove', this._onTouchMove);
        this.$el.removeEventListener('touchend', this._onTouchEnd);
        this.$el.removeEventListener('touchcancel', this._onTouchEnd);
      }
      document.body.classList.remove('ctr-sticky-atc-open');
    },

    _measure() {
      const h = Math.round(this.$el.getBoundingClientRect().height);
      if (h > 0) {
        document.documentElement.style.setProperty('--ctr-sticky-atc-h', h + 'px');
      }
    },

    _readDataAttributes({ preservePurchaseType = false } = {}) {
      const d = this.$el.dataset;

      this.variantId = parseInt(d.variantId, 10) || null;
      this.variantPrice = parseInt(d.price, 10) || 0;
      this.compareAtPrice = d.compareAtPrice ? parseInt(d.compareAtPrice, 10) : null;
      this.hasSellingPlans = d.hasSellingPlans === 'true';

      this.buttonText = d.buttonText || 'Add to cart';
      this.oneTimeLabel = d.oneTimeLabel || 'One-time';
      this.subscribeLabelBase = d.subscribeLabel || 'Subscribe & save';
      this.savingPct = parseInt(d.savingPct, 10) || 0;

      // INITIAL PURCHASE TYPE -- see the policy note at the top of this file.
      // Always one-time. Never read from a setting.
      if (!preservePurchaseType) {
        this.purchaseType = 'one-time';
      } else if (!this.hasSellingPlans) {
        this.purchaseType = 'one-time';
      }

      try {
        this.sellingPlanAllocations = JSON.parse(d.sellingPlanAllocations || '[]')
          .filter((a) => approvedPlanIds.has(Number(a.selling_plan_id)));
      } catch {
        this.sellingPlanAllocations = [];
      }

      this._buildPlans();

      /* R21b: the default is 4 weeks, by NAME, not the first allocation.
       *
       * data-default-selling-plan-id is `allocations | first`, which on this
       * store is the 1-week plan. Cadence is locked at 1/2/3/4 weeks with 4 as
       * the default, one ladder across all products. */
      const preserved =
        preservePurchaseType &&
        this.currentSellingPlanId &&
        this.sellingPlanAllocations.some((a) => a.selling_plan_id === this.currentSellingPlanId);
      if (!preserved) this.currentSellingPlanId = this._defaultPlanId();

      this._refreshPrices();
    },

    /* Build the selectable frequency list from the live allocations.
     *
     * De-duplicates by plan NAME: this store has several selling plan groups
     * offering identical intervals, so the raw list on the juice cleanse is 12
     * allocations for 6 distinct frequencies and renders "Every 1 week" twice.
     *
     * Drops 5 and 6 week plans. They must stay ALIVE -- 64 active 6-week
     * subscriptions are still billing and deleting the plans would cancel them
     * -- they simply must not be newly selectable. Matched on the parsed
     * number, not a substring, so "15 weeks" would not be caught by mistake.
     */
    _buildPlans() {
      const seen = new Set();
      const out = [];

      for (const alloc of this.sellingPlanAllocations || []) {
        if (!approvedPlanIds.has(Number(alloc.selling_plan_id))) continue;
        const name = (alloc.name || '').trim();
        if (!name) continue;

        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);

        const match = name.match(/(\d+)\s*week/i);
        const weeks = match ? parseInt(match[1], 10) : null;
        if (weeks === 5 || weeks === 6) continue;

        out.push({
          id: alloc.selling_plan_id,
          name,
          weeks,
          short: weeks ? weeks + (weeks === 1 ? ' week' : ' weeks') : name,
          popular: weeks === 4,
          price: alloc.price,
        });
      }

      out.sort((a, b) => (a.weeks == null ? 99 : a.weeks) - (b.weeks == null ? 99 : b.weeks));
      this.plans = out;
    },

    _defaultPlanId() {
      const four = this.plans.find((p) => p.weeks === 4);
      const pick = four || this.plans[0];
      return pick ? pick.id : null;
    },

    _setupObserver() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }

      const targetSel = this.$el.dataset.targetSelector || '[data-sticky-atc-target]';
      const target = document.querySelector(targetSel);
      if (!target) return;

      this._observer = new IntersectionObserver(
        ([entry]) => {
          this.pastTarget = !entry.isIntersecting;
          // Buy box back on screen -- forget any swipe-away.
          if (!this.pastTarget) this.dismissed = false;
        },
        { threshold: 0 }
      );
      this._observer.observe(target);
      this._syncVisibilityFromTarget(target);
    },

    _syncVisibilityFromTarget(target) {
      const rect = target.getBoundingClientRect();
      const intersects =
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < window.innerHeight &&
        rect.left < window.innerWidth;
      this.pastTarget = !intersects;
      if (!this.pastTarget) this.dismissed = false;
    },

    _refreshPrices() {
      if (this.purchaseType === 'subscription' && this.sellingPlanAllocations.length > 0) {
        const alloc =
          this.sellingPlanAllocations.find((a) => a.selling_plan_id === this.currentSellingPlanId) ||
          this.sellingPlanAllocations[0];
        if (alloc) {
          this.displayPrice = alloc.price;
          this.compareAtPrice = alloc.compare_at_price > alloc.price ? alloc.compare_at_price : null;
          return;
        }
      }
      this.displayPrice = this.variantPrice;
      this.compareAtPrice = null;
    },

    _broadcast() {
      document.dispatchEvent(
        new CustomEvent('ctr:buybox:change', {
          detail: {
            purchaseType: this.purchaseType,
            currentSellingPlanId: this.currentSellingPlanId,
            variantId: this.variantId,
            source: 'sticky-atc',
          },
        })
      );
    },

    /* ---------- subscribe toggle ---------- */

    get subscribed() {
      return this.purchaseType === 'subscription';
    },

    /* The percentage is dropped once subscribe is ON.
     *
     * Two reasons. It is redundant -- the exact saving is on the button as a
     * cash amount ("SAVE £10.50"), which is the more persuasive number. And
     * the right-hand slot grows from "One-time" to "Every 4 weeks" when the
     * toggle flips, so the label has less room in exactly the state where the
     * percentage matters least. Without this the label ellipsises to
     * "SUBSCRIBE & SAVE...". The row height never changes either way.
     */
    get subLabel() {
      const base = this.subscribeLabelBase || 'Subscribe & save';
      if (this.subscribed || !this.savingPct) return base;
      return base + ' ' + this.savingPct + '%';
    },

    // A switch that cannot change anything is worse than no switch: if every
    // plan on this variant was filtered out, row 1 hides entirely.
    get canSubscribe() {
      return this.hasSellingPlans && this.plans.length > 0;
    },

    toggleSubscribe() {
      if (!this.canSubscribe) return;

      this.purchaseType = this.subscribed ? 'one-time' : 'subscription';

      if (this.subscribed) {
        const valid = this.sellingPlanAllocations.some((a) => a.selling_plan_id === this.currentSellingPlanId);
        if (!valid) this.currentSellingPlanId = this._defaultPlanId();
      } else {
        this.freqOpen = false;
      }

      this._refreshPrices();
      this._broadcast();
    },

    selectPlan(id) {
      if (!id) return;
      this.currentSellingPlanId = id;
      this.freqOpen = false;
      this._refreshPrices();
      this._broadcast();
    },

    /* ---------- display ---------- */

    get currentPlanName() {
      const alloc = (this.sellingPlanAllocations || []).find(
        (a) => a.selling_plan_id === this.currentSellingPlanId
      );
      return alloc ? (alloc.name || '').trim() : '';
    },

    // The plan the customer is on may be outside `plans` -- a 5 or 6 week
    // subscriber arriving from the buy box still needs their cadence named.
    get freqLabel() {
      const name = this.currentPlanName;
      if (!name) return 'Choose';
      return name.replace(/^every\s+/i, 'Every ');
    },

    // One live number: the amount that will actually be charged.
    get ctaLabel() {
      const verb = this.subscribed ? 'Add' : this.buttonText || 'Add to cart';
      if (!this.displayPrice) return verb;
      return verb + ' · ' + this._formatMoney(this.displayPrice);
    },

    get saveAmount() {
      if (!this.subscribed) return 0;
      return Math.max(0, (this.variantPrice || 0) - (this.displayPrice || 0));
    },

    get saveLabel() {
      // Caps in the string, not in CSS: the chip sets text-transform:none so
      // it does not inherit the button's uppercase, which would also shout
      // the currency symbol's surroundings.
      return this.saveAmount > 0 ? 'SAVE ' + this._formatMoney(this.saveAmount) : '';
    },

    _formatMoney(cents) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'GBP',
        minimumFractionDigits: 2,
      }).format(cents / 100);
    },

    async addToCart(event) {
      if (this.loading || !this.variantAvailable || !this.variantId) return;
      this.loading = true;
      this.freqOpen = false;

      const body = { id: this.variantId, quantity: 1 };

      if (this.purchaseType === 'subscription' && this.currentSellingPlanId) {
        body.selling_plan = this.currentSellingPlanId;
      }

      // R-TAP (17 Sep 2026): open on the tap, not on the response. See the note
      // in assets/cart-drawer.js openForPendingAdd().
      const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      cartDrawer?.openForPendingAdd?.({ source: event?.currentTarget ?? this.$el?.querySelector('button') ?? null });

      // R57c-4: the registry discovery, the section params, the fetch, the
      // parse, the error branch and the post-mutation dispatch all moved into
      // cartController.add(). The old fallback walk over cart-items-component
      // went with them - cartSections.ids() already unions those in, so this
      // file was duplicating discovery that the registry does properly.
      try {
        if (!window.cartController?.add) {
          const error = new Error('cart-controller.js is not loaded; no cart request was sent');
          console.error('[ctr-sticky-atc]', error.message);
          window.ctrNotifyCartFailure(
            'Sorry, your basket could not be updated. Please try again.',
            'ctr-sticky-atc',
            error
          );
          return;
        }

        const result = await window.cartController.add(body, { source: 'ctr-sticky-atc' });

        // Shopify refused the add, or the request never produced a usable
        // response. Either way the controller has logged it, surfaced it and
        // re-read every cart surface from the server. R-TAP: the drawer is
        // already open by now and stays open - the banner is in front of it and
        // closing it would take the explanation away - but the pending mark is
        // lifted in the finally so it stops pulsing.
        // R-TAP-ANIM, round TAP-12 finding 1: NO unconditional open() here any more.
        // It reopened the drawer after the response on a basket that already had
        // items, which is what Charlie asked not to happen. The tap-time call
        // above is the only decision, and it flew the photo instead.
        if (!result.ok) return;
      } catch (err) {
        // R90b, Codex round 35 finding 6. An exception here means the add is
        // INDETERMINATE: the server may have taken it and the settle/reconcile
        // may be what failed. Indeterminate is treated as failed, and failed is
        // never silent. cartController.add() surfaces what it knows about, so
        // anything reaching this catch has been shown to nobody.
        console.error('[ctr-sticky-atc] Add to cart failed:', err);
        window.ctrNotifyCartFailure?.(
          'Sorry, your basket could not be updated. Please try again.',
          'ctr-sticky-atc',
          err
        );
      } finally {
        // A refused subscription add returns from cartController.add() WITHOUT
        // reconciling, so no cart update is coming to lift the pending mark.
        cartDrawer?.clearPendingAdd?.();
        this.loading = false;
      }
    },
  }));
});
